import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';
import { MemoriesApi } from 'app/domains/aya/apis/memories.api';
import { IMemory } from 'app/domains/aya/interfaces/memory.interface';

const MAX_CONTENT_LENGTH = 2000;
const SKELETON_COUNT = 3;

const MEMORY_ERROR_MESSAGES: { [code: string]: string } = {
  memory_not_found: 'Esta memória não existe mais.',
  validation_error: 'A memória deve ter entre 1 e 2000 caracteres.',
  memory_unavailable:
    'O serviço de memórias está indisponível no momento. Tente novamente em instantes.',
  internal_error: 'Não foi possível concluir a ação. Tente novamente.',
};

interface IMemoryRow {
  memory: IMemory;
  isRemoving: boolean;
}

@Component({
  selector: 'sgt-memories-section',
  templateUrl: './memories-section.component.html',
  styleUrls: ['./memories-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemoriesSectionComponent implements OnInit, OnDestroy {
  rows: IMemoryRow[] = [];
  isLoading = false;
  hasError = false;
  hasMore = false;
  isCreating = false;
  automaticCaptureEnabled = false;
  isSavingSettings = false;

  readonly maxLength = MAX_CONTENT_LENGTH;
  readonly skeletonRows = Array.from({ length: SKELETON_COUNT }, (_, index) => index);
  readonly newMemoryControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(MAX_CONTENT_LENGTH),
  ]);

  private destroy$ = new Subject<void>();

  constructor(
    private memoriesApi: MemoriesApi,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {}

  trackByMemoryId(_: number, row: IMemoryRow): string {
    return row.memory.id;
  }

  ngOnInit(): void {
    this.loadSettings();
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  create(): void {
    const content = (this.newMemoryControl.value ?? '').trim();
    if (this.isCreating || !content || content.length > MAX_CONTENT_LENGTH) {
      return;
    }

    this.isCreating = true;

    this.memoriesApi
      .create({ content })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: memory => {
          this.isCreating = false;
          this.rows = [this.buildRow(memory), ...this.rows];
          this.newMemoryControl.reset('');
          this.cdr.markForCheck();
        },
        error: error => {
          this.isCreating = false;
          this.toastService.error(this.errorMessage(error));
          this.cdr.markForCheck();
        },
      });
  }

  toggleAutomaticMemory(enabled: boolean): void {
    if (this.isSavingSettings) {
      return;
    }

    const previous = this.automaticCaptureEnabled;
    this.automaticCaptureEnabled = enabled;
    this.isSavingSettings = true;

    this.memoriesApi
      .updateSettings({ automaticCaptureEnabled: enabled })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: settings => {
          this.isSavingSettings = false;
          this.automaticCaptureEnabled = settings.automaticCaptureEnabled;
          this.cdr.markForCheck();
        },
        error: error => {
          this.isSavingSettings = false;
          this.automaticCaptureEnabled = previous;
          this.toastService.error(this.errorMessage(error));
          this.cdr.markForCheck();
        },
      });
  }

  remove(row: IMemoryRow): void {
    if (row.isRemoving) {
      return;
    }

    row.isRemoving = true;

    this.memoriesApi
      .remove(row.memory.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dropRow(row.memory.id);
          this.cdr.markForCheck();
        },
        error: error => {
          row.isRemoving = false;
          this.handleMutationError(error, row.memory.id);
          this.cdr.markForCheck();
        },
      });
  }

  private load(): void {
    this.isLoading = true;
    this.hasError = false;

    this.memoriesApi
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: output => {
          this.isLoading = false;
          this.rows = output.items.map(memory => this.buildRow(memory));
          this.hasMore = output.hasMore;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.hasError = true;
          this.rows = [];
          this.cdr.markForCheck();
        },
      });
  }

  private loadSettings(): void {
    this.isSavingSettings = true;

    this.memoriesApi
      .getSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: settings => {
          this.isSavingSettings = false;
          this.automaticCaptureEnabled = settings.automaticCaptureEnabled;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isSavingSettings = false;
          this.cdr.markForCheck();
        },
      });
  }

  private handleMutationError(error: unknown, memoryId: string): void {
    if (this.errorCode(error) === 'memory_not_found') {
      this.dropRow(memoryId);
    }
    this.toastService.error(this.errorMessage(error));
  }

  private dropRow(memoryId: string): void {
    this.rows = this.rows.filter(row => row.memory.id !== memoryId);
  }

  private buildRow(memory: IMemory): IMemoryRow {
    return {
      memory,
      isRemoving: false,
    };
  }

  private errorCode(error: unknown): string | null {
    const body = (error as { error?: { code?: string; error?: { code?: string } } })?.error;
    return body?.code ?? body?.error?.code ?? null;
  }

  private errorMessage(error: unknown): string {
    const code = this.errorCode(error);
    return (
      (code && MEMORY_ERROR_MESSAGES[code]) || 'Não foi possível concluir a ação.'
    );
  }
}
